import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  CircularProgress,
  Stack,
  Container,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useAppSelector, useResponsive, useTerminology } from "@/hooks";
import PageHeader from "@/components/PageHeader";
import { useFetchMenusQuery } from "@/features/organizations/api/menusApi";
import { useFetchContentQuery } from "@/features/organizations/api/contentApi";
import { useFetchTeamMembersQuery } from "@/api/accessApi";
import { useFetchOrgUsersQuery } from "@/features/organizations/api/orgCustomersApi";
import { useUpdateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  MagicWand01Icon,
  MenuRestaurantIcon,
  UserAdd01Icon,
  Rocket01Icon,
  Scooter03Icon,
  TicketStarIcon,
  Chart03Icon,
  Settings02Icon,
} from "@hugeicons/core-free-icons";
import StepsCard from "./StepsCard.tsx";
import { useIncomingOrders } from "../organization-orders/IncomingOrdersProvider.tsx";
import { LocalStorage } from "@/constants/localStorage";


const FirstSteps = () => {
  const container = useAppSelector((state) => state.ui?.container);
  const { organizationId } = useParams<{ organizationId: string }>();
  const { isMobile } = useResponsive();
  const { t } = useTerminology();
  const organization = useAppSelector((state) => state.organizations[organizationId || ""]);
  const profile = organization?.productAdoptionProfile?.featuresEnabled;

  const skipMenus = !organizationId || Boolean(profile?.hasCreatedMenu);
  const skipContent = !organizationId || Boolean(profile?.hasCreatedContent);
  const skipTeam = !organizationId || Boolean(profile?.hasInvitedStaff || profile?.hasIgnoredStaff);
  const skipCustomers = !organizationId || Boolean(profile?.hasInitialCustomers);


  const optimisticIgnore = useRef({
    hasIgnoredStaff: false,
    hasIgnoredDelivery: false,
    hasIgnoredLoyalty: false,
  });
  const lastSyncPayload = useRef<string | null>(null);

  const { data: menusData, isLoading: isLoadingMenus } = useFetchMenusQuery(organizationId || skipToken, { skip: skipMenus });
  const { data: contentData, isLoading: isLoadingContent } = useFetchContentQuery(organizationId || skipToken, { skip: skipContent });
  const { data: teamMembers, isLoading: isLoadingTeam } = useFetchTeamMembersQuery(organizationId || skipToken, { skip: skipTeam });
  const { data: customersData, isLoading: isLoadingCustomers } = useFetchOrgUsersQuery(organizationId || skipToken, { skip: skipCustomers });

  const [updateOrganization, { isLoading: isUpdatingProfile }] = useUpdateOrganizationMutation();

  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [hasInitializedExpanded, setHasInitializedExpanded] = useState(false);

  // Chrome Dialog Freeze
  const { openChromeFreezeDialog } = useIncomingOrders();
  //


  const sections = [
    {
      title: "Despega",
      description: `Haz que tu ${t("establishment")} exista en internet. Completa lo esencial para que tus clientes puedan encontrarte.`,
      steps: [
        {
          id: "branding",
          title: "Branding",
          description: `Configura la identidad visual de tu ${t("establishment")}. Sube tu logotipo, isotipo y establece los colores principales para personalizar tu sitio web.`,
          icon: (
            <HugeiconsIcon
              icon={MagicWand01Icon}
              size={20}
            />
          ),
          actionText: "Branding",
          destination: `/comercios/${organizationId}/detalles`,
          isCompleted:
            Boolean(
              organization?.logo ||
              organization?.isotype
            ),
          allowIgnore: false,
        },
        {
          id: "contenido",
          title: "Contenido",
          description: `Crea tu primer ${t("catalog")} y sube tus primeros ${t("product", 2)} con fotos y precios.`,
          icon: (
            <HugeiconsIcon
              icon={MenuRestaurantIcon}
              size={20}
            />
          ),
          actionText: `${t("catalog", 1, true)}`,
          destination: `/comercios/${organizationId}/menus`,
          isCompleted: Boolean(
            (
              profile?.hasCreatedMenu &&
              profile?.hasCreatedContent &&
              profile?.hasConfiguredDishes
            )
            ||
            (
              (menusData?.length ?? 0) > 0 &&
              (contentData?.length ?? 0) > 0
            )
          ),
          allowIgnore: false,
        },
        {
          id: "lanzamiento",
          title: "Lanzamiento",
          description: `Activa tu sitio web en línea y comparte el enlace de tu ${t("catalog")} con tus clientes para comenzar a recibir pedidos.`,
          icon: (
            <HugeiconsIcon
              icon={Rocket01Icon}
              size={20}
            />
          ),
          actionText: "Sitio Web",
          destination: `/comercios/${organizationId}/mi-sitio`,
          isCompleted: Boolean(
            organization?.alias &&
            organization?.website
          ),
          allowIgnore: false,
        },
      ],
    },
    {
      title: "Impúlsate",
      description: "Ahora es momento de facilitar las compras.",
      steps: [
        {
          id: "delivery",
          title: "Delivery",
          description: "Define tus zonas de cobertura, tarifas de envío y horarios de atención para recibir pedidos a domicilio.",
          icon: (
            <HugeiconsIcon
              icon={Scooter03Icon}
              size={20}
            />
          ),
          actionText: "Envíos",
          destination: `/comercios/${organizationId}/configuraciones`,
          isCompleted:
            Boolean(
              organization?.settings?.services?.delivery?.isEnabled ||
              profile?.hasIgnoredDelivery
            ),
          allowIgnore: false,
        },
        {
          id: "colaboracion",
          title: "Colaboración",
          description: `Invita a tu equipo de trabajo y asigna roles para ayudarte a gestionar los pedidos y tu ${t("establishment")}.`,
          icon: (
            <HugeiconsIcon
              icon={UserAdd01Icon}
              size={20}
            />
          ),
          actionText: "Colaboradores",
          destination: `/comercios/${organizationId}/accesos`,
          isCompleted:
            Boolean(
              profile?.hasInvitedStaff ||
              profile?.hasIgnoredStaff ||
              (teamMembers?.length ?? 0) > 1
            ),
          allowIgnore: false,
        },
        {
          id: "chrome-freeze",
          title: "Evitar suspensión de Chrome",
          description: "Configura tu cuenta para evitar que Chrome suspenda esta página cuando permanezca inactiva.",
          icon: (
            <HugeiconsIcon
              icon={Settings02Icon}
              size={20}
            />
          ),
          actionText: "Configurar",
          onAction: openChromeFreezeDialog,
          isCompleted:
            localStorage.getItem(
              LocalStorage.CHROME_FREEZE_CONFIGURED
            ) === "true",
          allowIgnore: false,
        }
      ],
    },
    {
      title: "Escala",
      description: "Activa herramientas para crecer.",
      steps: [
        {
          id: "fidelizacion",
          title: "Fidelización",
          description: "Activa tu programa de puntos y premia la lealtad de tus clientes para incentivar que vuelvan a comprar.",
          icon: (
            <HugeiconsIcon
              icon={TicketStarIcon}
              size={20}
            />
          ),
          actionText: "Fidelizar",
          destination: `/comercios/${organizationId}/loyalty`,
          isCompleted:
            Boolean(
              organization?.settings?.loyaltyProgram?.isEnabled ||
              profile?.hasIgnoredLoyalty
            ),
          allowIgnore: false,
        },
        {
          id: "crecimiento",
          title: "Crecimiento",
          description: `Monitorea el rendimiento de tu ${t("establishment")} en tiempo real para optimizar tus ventas.`,
          icon: (
            <HugeiconsIcon
              icon={Chart03Icon}
              size={20}
            />
          ),
          actionText: "Reportes",
          destination: `/comercios/${organizationId}/dashboard`,
          isCompleted:
            Boolean(
              profile?.hasInitialCustomers ||
              (customersData?.length ?? 0) > 0
            ),
          allowIgnore: false,
        },
      ],
    },
  ];

  useEffect(() => {
    setHasInitializedExpanded(false);
  }, [organizationId]);

  useEffect(() => {
    const isMenusLoading =
      menusData === undefined && !skipMenus;

    const isContentLoading =
      contentData === undefined && !skipContent;

    const isTeamLoading =
      teamMembers === undefined && !skipTeam;

    const isCustomersLoading =
      customersData === undefined && !skipCustomers;

    if (
      !organization ||
      hasInitializedExpanded ||
      isMenusLoading ||
      isContentLoading ||
      isTeamLoading ||
      isCustomersLoading
    ) {
      return;
    }

    const allSteps =
      sections.flatMap(
        (section) => section.steps
      );

    const firstIncomplete =
      allSteps.find(
        (step) => !step.isCompleted
      );

    setExpandedStep(
      firstIncomplete?.id ?? null
    );
    setHasInitializedExpanded(true);

  }, [
    organization,
    hasInitializedExpanded,
    menusData,
    contentData,
    teamMembers,
    customersData,
    skipMenus,
    skipContent,
    skipTeam,
    skipCustomers,
  ]);




  const handleStepClick = (id: string) => {
    setExpandedStep(
      (prev) => prev === id ? null : id
    );
  };

  const isLoading =
    isLoadingMenus ||
    isLoadingContent ||
    isLoadingTeam ||
    isLoadingCustomers;


  if (
    isLoading &&
    !organization
  ) {

    return (
      <Stack
        sx={{
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Stack>
    );

  }
  return (
    <Stack spacing={3} height={'100%'} overflow={'scroll'} >
      <PageHeader
        title="Tu camino al éxito"
        banner="https://firebasestorage.googleapis.com/v0/b/menuapp-b44a8.appspot.com/o/assets%2Fnew-first-steps-banner.webp?alt=media&token=70455b1a-5891-4fea-8ca8-202041d928a7"
      />
      <Stack
        sx={{
          flex: 1,
        }}
      >
        <Container
          maxWidth={container === 'fixed' ? 'lg' : false}
          sx={{ height: '100%' }}
        >
          <Grid
            container
            spacing={3}
          >

            {sections.map(
              (section) => (

                <Grid
                  key={section.title}
                  size={{
                    sm: 12,
                    md: 4
                  }}
                  sx={{
                    height: isMobile ? '100%' : undefined,
                    width: isMobile ? '100%' : undefined

                  }}
                >

                  <StepsCard
                    section={section}
                    expandedStep={expandedStep}
                    onStepClick={handleStepClick}
                  />
                </Grid>

              )
            )}
          </Grid>
        </Container>
      </Stack>
    </Stack>
  )
};

export default FirstSteps;
